<template>
  <div class="modal fade" id="modal" ref="modal" data-bs-backdrop="static" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">

        <div class="bg-theme modal-header pe-4 text-white" style="border-radius: 16px 16px 0 0;">
          <div>
            <h4 class="modal-title fw-bold mb-0">
              <i class="fa-solid fa-bell-concierge me-2" />
              {{ $t('Service Requests') }}
            </h4>
            <div class="small opacity-75 mt-1">
              <i class="fa-solid fa-chair me-1" />
              {{ $t('Table') }} {{ name }}
            </div>
          </div>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" />
        </div>

        <div class="modal-body px-4 py-4">

          <!-- Filter pills + refresh -->
          <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
            <button
              v-for="opt in filterOptions"
              :key="'f-' + opt.value"
              type="button"
              class="btn btn-sm rounded-pill px-3 fw-bold"
              :class="filter === opt.value ? 'btn-theme text-white' : 'btn-outline-secondary'"
              @click="setFilter(opt.value, $event)"
            >
              <i v-if="opt.icon" :class="opt.icon" class="me-1" />
              {{ $t(opt.label) }}
              <span v-if="counts[opt.value]" class="badge bg-white text-dark ms-1">
                {{ counts[opt.value] }}
              </span>
            </button>

            <div class="ms-auto">
              <button
                class="btn btn-sm btn-light rounded-pill px-3 fw-bold"
                :disabled="loading"
                @click="fetchData(false)"
              >
                <i :class="loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'" />
                <span class="ms-1 d-none d-sm-inline">{{ $t('Refresh') }}</span>
              </button>
            </div>
          </div>

          <div class="requests-scroll">

          <!-- Loading skeleton -->
          <div v-if="loading && requests.length === 0" class="d-flex flex-column gap-2">
            <div
              v-for="i in 3" :key="'rsk-' + i"
              class="card border-0 shadow-sm rounded-3"
            >
              <div class="card-body p-3">
                <div class="skeleton-shimmer rounded mb-2" style="height: 16px; width: 40%;" />
                <div class="skeleton-shimmer rounded mb-2" style="height: 12px; width: 60%;" />
                <div class="skeleton-shimmer rounded" style="height: 32px; width: 100%;" />
              </div>
            </div>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="text-center text-muted py-4">
            <i class="fa-solid fa-circle-exclamation fa-2x text-danger mb-2 opacity-75" />
            <p class="fw-bold mb-1">{{ $t('Failed to load requests') }}</p>
            <p class="small mb-3">{{ error }}</p>
            <button class="btn btn-theme text-white rounded-pill px-3" @click="fetchData(false)">
              <i class="fa-solid fa-rotate me-2" />{{ $t('Try again') }}
            </button>
          </div>

          <!-- Empty -->
          <div v-else-if="filteredRequests.length === 0" class="text-center text-muted py-4">
            <i class="fa-solid fa-inbox fa-3x mb-3 opacity-50" />
            <p class="fw-bold mb-0">{{ $t('No requests') }}</p>
            <p class="small">{{ emptyHint }}</p>
          </div>

          <!-- Requests list -->
          <div v-else class="d-flex flex-column gap-2">
            <div
              v-for="req in filteredRequests"
              :key="'req-' + req.id"
              class="card border-0 shadow-sm rounded-3"
              :class="{ 'opacity-75': req.state === 'cancelled' || req.state === 'completed' }"
            >
              <div class="card-body p-3">

                <!-- Header: icon + name + state -->
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div class="d-flex align-items-start">
                    <div
                      class="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                      :class="iconBgClass(req.state)"
                      style="width: 38px; height: 38px;"
                    >
                      <i :class="iconClass(req.type_icon)" />
                    </div>
                    <div>
                      <div class="fw-bold">{{ $t(req.type_name) }}</div>
                      <div class="text-muted small">
                        <i class="fa-regular fa-clock me-1" />
                        {{ $storeIndex.getTimeDiff(req.created_at) }}
                      </div>
                      <div v-if="req.note" class="mt-1">
                        <span class="badge bg-light text-dark border" style="font-size: 0.75rem;">
                          <i class="fa-solid fa-comment-dots me-1 text-theme" />{{ req.note }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span class="badge" :class="stateBadgeClass(req.state)">
                    <i :class="stateIcon(req.state)" class="me-1" />
                    {{ $t(req.state_text || stateLabel(req.state)) }}
                  </span>
                </div>

                <!-- Timeline: who acknowledged / completed / cancelled and when -->
                <div v-if="hasTimeline(req)" class="small text-muted mb-2 border-top pt-2">
                  <div v-if="req.acknowledged_at">
                    <i class="fa-solid fa-check me-1 text-success" />
                    {{ $t('Acknowledged') }} {{ formatTime(req.acknowledged_at) }}
                    <span v-if="req.acknowledged_by"> · {{ req.acknowledged_by }}</span>
                  </div>
                  <div v-if="req.completed_at">
                    <i class="fa-solid fa-check-double me-1 text-success" />
                    {{ $t('Completed') }} {{ formatTime(req.completed_at) }}
                    <span v-if="req.completed_by"> · {{ req.completed_by }}</span>
                  </div>
                  <div v-if="req.cancelled_at">
                    <i class="fa-solid fa-xmark me-1 text-danger" />
                    {{ $t('Cancelled') }} {{ formatTime(req.cancelled_at) }}
                    <span v-if="req.cancelled_by"> · {{ req.cancelled_by }}</span>
                  </div>
                </div>

                <!-- Action buttons (per state machine) -->
                <div v-if="canAct(req.state)" class="d-flex flex-wrap gap-2">
                  <button
                    v-if="req.state === 'pending'"
                    class="btn btn-sm btn-outline-primary fw-bold rounded-3"
                    :disabled="!!busy[req.id]"
                    @click="onAcknowledge(req)"
                  >
                    <i v-if="busy[req.id] === 'ack'" class="fa-solid fa-spinner fa-spin me-1" />
                    <i v-else class="fa-solid fa-check me-1" />
                    {{ $t('Acknowledge') }}
                  </button>

                  <button
                    v-if="req.state === 'pending' || req.state === 'acknowledged'"
                    class="btn btn-sm btn-success fw-bold rounded-3"
                    :disabled="!!busy[req.id]"
                    @click="onComplete(req)"
                  >
                    <i v-if="busy[req.id] === 'complete'" class="fa-solid fa-spinner fa-spin me-1" />
                    <i v-else class="fa-solid fa-check-double me-1" />
                    {{ $t('Mark done') }}
                  </button>

                  <button
                    v-if="req.state === 'pending' || req.state === 'acknowledged'"
                    class="btn btn-sm btn-outline-danger fw-bold rounded-3 ms-auto"
                    :disabled="!!busy[req.id]"
                    @click="onCancel(req)"
                  >
                    <i v-if="busy[req.id] === 'cancel'" class="fa-solid fa-spinner fa-spin me-1" />
                    <i v-else class="fa-solid fa-xmark me-1" />
                    {{ $t('Cancel') }}
                  </button>
                </div>

              </div>
            </div>
          </div>

          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { SessionRequests } from '@/services/endpoint/pos.service';

/**
 * Same icon-string mapping used on the customer side — keep them in sync
 * so the icon a customer tapped is what staff sees here.
 */
const ICON_MAP = {
  bell:      'fa-solid fa-bell-concierge',
  water:     'fa-solid fa-glass-water',
  rice:      'fa-solid fa-bowl-rice',
  utensils:  'fa-solid fa-utensils',
  tissue:    'fa-solid fa-toilet-paper',
  chili:     'fa-solid fa-pepper-hot',
  snowflake: 'fa-solid fa-snowflake',
  plate:     'fa-solid fa-plate-wheat',
  warning:   'fa-solid fa-triangle-exclamation',
  note:      'fa-solid fa-pen-to-square',
};

export default {
  name: 'ModalServiceRequests',
  props: {
    token: { type: String, required: true },
    name:  { type: String, default: '' },
  },
  emits: ['close', 'changed'],
  data() {
    return {
      loading: true,
      error: null,
      requests: [],
      filter: 'pending',     // sensible default — staff inbox
      // Per-request action lock: { [reqId]: 'ack' | 'complete' | 'cancel' }
      // Prevents double-tap firing two transitions at once.
      busy: {},
      filterOptions: [
        { value: 'all',          label: 'All',          icon: 'fa-solid fa-list' },
        { value: 'pending',      label: 'Pending',      icon: 'fa-solid fa-hourglass-half' },
        { value: 'acknowledged', label: 'Acknowledged', icon: 'fa-solid fa-check' },
        { value: 'completed',    label: 'Completed',    icon: 'fa-solid fa-check-double' },
        { value: 'cancelled',    label: 'Cancelled',    icon: 'fa-solid fa-xmark' },
      ],
    };
  },
  computed: {
    /** Filter visible cards by the active pill (id desc preserved from server). */
    filteredRequests() {
      if (this.filter === 'all') return this.requests;
      return this.requests.filter(r => r.state === this.filter);
    },
    /** Counts per state for pill badges. */
    counts() {
      const out = { all: this.requests.length };
      for (const r of this.requests) {
        out[r.state] = (out[r.state] || 0) + 1;
      }
      return out;
    },
    emptyHint() {
      const map = {
        pending:      this.$t('No new requests'),
        acknowledged: this.$t('Nothing in progress'),
        completed:    this.$t('Nothing completed yet'),
        cancelled:    this.$t('Nothing cancelled'),
        all:          this.$t('No requests for this table'),
      };
      return map[this.filter] || '';
    },
  },
  methods: {
    /**
     * Centralised fetch. Server already returns id-desc; we just hold the
     * full list and filter client-side so switching pills is instant.
     */
    async fetchData(silent = false) {
      if (!silent) this.loading = true;
      this.error = null;
      try {
        const res = await SessionRequests.list(this.token);
        this.requests = res.data?.data ?? [];
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || this.$t('Network error');
        if (!silent && err?.response?.data?.message) {
          this.$toast?.warn?.(err.response.data.message);
        }
      } finally {
        this.loading = false;
      }
    },

    setFilter(value, ev) {
      // Touch-device hover-stuck guard, matching the customer-side pills.
      ev?.currentTarget?.blur?.();
      this.filter = value;
    },

    // --- State transitions ----------------------------------------------
    /**
     * Three near-identical PUT wrappers; collapse the boilerplate so the
     * call sites stay clean. Each one optimistically updates the cached
     * row from the server response, then notifies the parent so the
     * table card's `is_request` flag refreshes on the next list fetch.
     */
    async transition(req, action, label) {
      if (this.busy[req.id]) return;
      this.busy = { ...this.busy, [req.id]: action };
      try {
        const apiCall = ({
          ack:      () => SessionRequests.acknowledge(this.token, req.id),
          complete: () => SessionRequests.complete(this.token, req.id),
          cancel:   () => SessionRequests.cancel(this.token, req.id),
        })[action];
        const res = await apiCall();
        const updated = res.data?.data;
        if (updated) {
          // Replace in-place so filter/order stays intact.
          const idx = this.requests.findIndex(r => r.id === updated.id);
          if (idx !== -1) this.requests.splice(idx, 1, { ...this.requests[idx], ...updated });
        }
        this.$toast?.success?.('<b>' + this.$t('Success') + '</b> ' + this.$t(label));
        // Tell parent so it can refresh the table list (is_request flag).
        this.$emit('changed');
      } catch (err) {
        if (err?.response?.data?.message) {
          this.$toast?.warn?.(err.response.data.message);
        }
      } finally {
        this.busy = { ...this.busy, [req.id]: null };
      }
    },
    onAcknowledge(req) { return this.transition(req, 'ack',      'Request acknowledged'); },
    onComplete(req)    { return this.transition(req, 'complete', 'Request completed'); },
    onCancel(req) {
      // Confirm cancel via SweetAlert — staff might be reflexively tapping
      // and the API has no "uncancel" path.
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('Cancel this request')+'?',
        html: '<div class="text-start small">' + this.$t('Cancelled requests cannot be reopened. The customer will see it as cancelled.') + '</div>',
        showCancelButton: true,
        cancelButtonText: this.$t('Back'),
        confirmButtonText: this.$t('Cancel request'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true
      }).then(res => {
        if (!res.isConfirmed) return;
        this.transition(req, 'cancel', 'Request cancelled');
      });
    },

    // --- Display helpers ------------------------------------------------
    iconClass(name) {
      return ICON_MAP[name] || 'fa-solid fa-circle-question';
    },
    iconBgClass(state) {
      // Soft background ring around the icon, tinted by state.
      return ({
        pending:      'bg-secondary bg-opacity-10 text-secondary',
        acknowledged: 'bg-success bg-opacity-10 text-success',
        completed:    'bg-info bg-opacity-10 text-info',
        cancelled:    'bg-danger bg-opacity-10 text-danger',
      })[state] || 'bg-light text-muted';
    },
    canAct(state) {
      return state === 'pending' || state === 'acknowledged';
    },
    hasTimeline(req) {
      return !!(req.acknowledged_at || req.completed_at || req.cancelled_at);
    },

    stateBadgeClass(state) {
      return ({
        pending:      'bg-secondary',
        acknowledged: 'bg-success',
        completed:    'bg-info text-dark',
        cancelled:    'bg-danger',
      })[state] || 'bg-secondary';
    },
    stateIcon(state) {
      return ({
        pending:      'fa-solid fa-hourglass-half',
        acknowledged: 'fa-solid fa-check',
        completed:    'fa-solid fa-check-double',
        cancelled:    'fa-solid fa-xmark',
      })[state] || 'fa-solid fa-circle';
    },
    stateLabel(state) {
      return ({
        pending: 'Pending', acknowledged: 'Acknowledged',
        completed: 'Completed', cancelled: 'Cancelled',
      })[state] || state;
    },

    formatTime(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch { return ''; }
    },
  },
  mounted() {
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('shown.bs.modal', () => this.fetchData());
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
  },
};
</script>

<style scoped>
.requests-scroll {
  max-height: 60vh;
  overflow-y: auto;
  /* Offset the scrollbar so cards don't shift when it appears. */
  padding-right: 6px;
  margin-right: -6px;
}
.skeleton-shimmer {
  background: linear-gradient(90deg, #eceff2 0%, #f5f7f9 50%, #eceff2 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.25s ease-in-out infinite;
  display: block;
}
@keyframes pos-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
